import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, from, Observable, switchMap, tap, throwError } from 'rxjs';
import { SnackTypeMsg } from '../model/snackmsg/snackmsg.model';
import { UTILS } from '../utils/methods/Utils';
import { DataService } from './data.service';
import { TranslateService } from '@ngx-translate/core';
import { KeycloakService } from 'keycloak-angular';
import { environment } from 'src/environments/environment';
import { SecurityService } from './security.service';

const excludedResponses = [
  { url: "/admin/library/document/approval", responses: [HttpStatusCode.NotFound] },
  { url: "/admin/library/document/select", responses: [HttpStatusCode.NotFound] },
  { url: "/admin/user/menu/email", responses: [HttpStatusCode.NotFound] },
  { url: "/admin/digitalfile/typedocument/select", responses: [HttpStatusCode.NotFound] },
  { url: "/admin/digitalfile/type", responses: [HttpStatusCode.NotFound] },
  { url: "/admin/calendar/typecalendar", responses: [HttpStatusCode.NotFound] },
  { url: "/admin/calendar/admincalendar", responses: [HttpStatusCode.NotFound] },
  { url: "/admin/calendar/admincalendar", responses: [HttpStatusCode.NotFound] },
  { url: "/admin/catalogs/imagesystem/ids", responses: [HttpStatusCode.NotFound] },
  { url: "/admin/catalogs/service/action", responses: [HttpStatusCode.InternalServerError, HttpStatusCode.BadGateway] },
  { url: "/admin/student/notice", responses: [HttpStatusCode.Conflict] },
  { url: "/admin/student/status", responses: [HttpStatusCode.Conflict] }
];

@Injectable({
  providedIn: 'root'
})
export class InterceptorService implements HttpInterceptor {

  constructor(
    private readonly dataService: DataService,
    private readonly translateService: TranslateService,
    private readonly keycloak: KeycloakService,
    private readonly securityService: SecurityService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    return from(this.keycloak.updateToken(30)).pipe(
      switchMap(() => {
        const token = this.keycloak.getKeycloakInstance().token;

        const url = req.url.includes('?')
          ? req.url.replace('?&', '?') // NOSONAR
          : req.url.replace('&', '?'); // NOSONAR

        const modifiedReq = req.clone({
          url,
          setHeaders: {
            'Request-TYPE': 'admin',
            Authorization: `Bearer ${token}`
          },
          setParams: {
            origen: environment.origen,
            portal: environment.portal,
            origin: environment.origen
          }
        });

        return next.handle(modifiedReq).pipe(
          tap({
            error: (err: HttpErrorResponse) => this.validateError(err)
          })
        );
      }),
      catchError((err: any) => {

        const isBadRequestSecureAuth = this.getIsBadRequestSecureAuth(err);

        if (err.status === HttpStatusCode.Unauthorized || isBadRequestSecureAuth) {
          this.redirectLogin();
          return throwError(() => new Error('Token inválido, redirigiendo al login'));
        }
        return throwError(() => err);
      })
    );


  }

  redirectLogin() {
    this.securityService.logout();
  }

  getIsBadRequestSecureAuth(err: HttpErrorResponse) {

    if (err.status !== HttpStatusCode.BadRequest) {
      return false;
    }

    return err.url?.includes("/protocol/openid-connect/token");


  }

  validateError(err: HttpErrorResponse) {

    if (err instanceof HttpErrorResponse && err.status >= 300 && err.status < 400) {
      return;
    }

    const isExcludedError = this.excludedError(err);

    if (isExcludedError) {
      return;
    }

    this.validateOtherError(err);

  }

  validateOtherError(err: HttpErrorResponse) {

    let msg = this.translateService.instant("interceptor-generic-error");

    const isIncludedMessage = [
      err?.error?.hasOwnProperty("message"),
      err?.error?.hasOwnProperty("Message")
    ]

    if (UTILS.getOrValidation(isIncludedMessage, true)) {
      msg = err.error?.message || err.error?.Message;
    }

    this.openErrorDialog(msg);
  }

  excludedError(err: HttpErrorResponse) {

    if (err.status === HttpStatusCode.Unauthorized) {
      this.redirectLogin();
      return true;
    }

    for (const excluded of excludedResponses) {
      if (err.url!.includes(excluded.url)) {
        return excluded.responses.find((code) => code === err.status);
      }
    }

    return false;
  }

  openErrorDialog(msg: string) {

    this.dataService.setGeneralNotificationMessage({
      msg,
      type: SnackTypeMsg.Error
    });

  }

}
